import { ReactNode, forwardRef, useCallback } from 'react'

import { IconDefinition, faCheck, faChevronLeft } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Form, FormInstance } from 'antd'
import AntdTitle from 'antd/es/typography/Title'
import { HStack, Icon, Link, T, Tooltip } from 'govwell-ui'
import { IconKey } from 'govwell-ui/components/Icon/util'
import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { back, navigate } from '@redwoodjs/router'

import { RequiredMark } from 'src/components/form/RequiredMark'
import { backwardsCompatibleAddressUrl } from 'src/utils/gis'

import { BackwardsCompatibleLocationInformationFragment } from '../../../types/graphql'
import { ColorTheme } from '../../constants/theme/colors'
import { ColorToken } from '../../constants/theme/types'
import { useCurrentBreakpoint } from '../../hooks/use-current-breakpoint'
import { HoverProps } from '../../hooks/use-hover'
import { useTheme } from '../../hooks/use-theme'
import { LottiesLoading } from '../../lotties/loading'
import LottieAnimation from '../Lottie'
import { HorizontalLine } from '../StyledComponents'
import Text, { TextSize } from '../Typography/Text'

export interface StylePropsMobile {
  $small: boolean
}

export const IconContentRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`

interface FormDualInputContainerProps {
  children: ReactNode
}

const StyledFormDualInputContainer = styled.div<StylePropsMobile>`
  display: flex;
  gap: 24px;
  ${(props) => props.$small && 'flex-direction: column;'}
`
export const FormDualInputContainer = (props: FormDualInputContainerProps) => {
  const { children } = props
  const { isSmallerThanOrEqual } = useCurrentBreakpoint()
  return (
    <StyledFormDualInputContainer $small={isSmallerThanOrEqual('sm')}>
      {children}
    </StyledFormDualInputContainer>
  )
}

export const getContainerStyleProps = (theme: ColorTheme) => {
  return `
    border-top: 1px solid ${theme.colorBordersecondary};
    border-right: 1px solid ${theme.colorBordersecondary};
    border-bottom: 1px solid ${theme.colorBordersecondary};
    border-left: 1px solid ${theme.colorBordersecondary};
    background-color: ${theme.colorFillAlter};
    border-radius: 8px;
`
}

export const removeContainerStyleProps = () => {
  return `
    border: unset;
    background-color: inherit;
    border-radius: 0px;
  `
}

export const getLightContainerStyleProps = (theme: ColorTheme, error?: boolean) => {
  return `
    border: 1px solid ${error ? theme.colorErrorBase : theme.colorBordersecondary};
    background-color: ${theme.colorWhite};
    border-radius: 8px;
  `
}

type PageTitleProps = {
  actions?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  backButtonHref?: string
  icon?: IconKey | null
  showRouterBackButton?: boolean
} & (
  | {
      backButtonHref?: string
      onBackButtonClick?: never
    }
  | {
      backButtonHref?: never
      onBackButtonClick: () => void
    }
)

export const PageTitle = (props: PageTitleProps) => {
  const {
    actions,
    backButtonHref,
    icon,
    onBackButtonClick,
    showRouterBackButton,
    subtitle,
    title,
  } = props

  const showBackButton = !!backButtonHref || !!(showRouterBackButton && window.history.length > 1)
  const handleBackButtonClick = useCallback(() => {
    if (onBackButtonClick) {
      onBackButtonClick()
      return
    }
    if (backButtonHref) {
      navigate(backButtonHref)
      return
    }
    back()
  }, [backButtonHref, onBackButtonClick])

  return (
    <HStack align="center" gap={4}>
      {showBackButton && (
        <Button
          onClick={handleBackButtonClick}
          id="back"
          type="default"
          icon={<FontAwesomeIcon icon={faChevronLeft} />}
          className="hidden md:block"
        >
          Back
        </Button>
      )}
      <HStack gap={4} align="center" justify="space-between" wrap="wrap">
        <div className="grid grid-cols-[auto_1fr] items-center">
          <div className="row-span-2">
            {icon && (
              <T.Body className="text-xl">
                <Icon icon={icon} className="pr-2 font-semibold text-neutral-700" />
              </T.Body>
            )}
          </div>
          {typeof title === 'string' ? <T.H1>{title}</T.H1> : title}
          {!!subtitle && <T.Body className="text-lg text-neutral-700">{subtitle}</T.Body>}
        </div>
        <HStack flex={1} justify="end">
          {actions}
        </HStack>
      </HStack>
    </HStack>
  )
}

type CardTitleIconProps = {
  colorToken?: ColorToken
  tooltip?: string
  icon: IconDefinition
  fontSize?: number
}

export const CardTitleIcon = ({ colorToken, tooltip, icon, fontSize }: CardTitleIconProps) => {
  const { isSmallerThanOrEqual } = useCurrentBreakpoint()
  const small = isSmallerThanOrEqual('md')
  const { getTokenVal } = useTheme()

  if (!icon || small) {
    return null
  }

  return (
    <Tooltip content={tooltip}>
      <FontAwesomeIcon
        icon={icon}
        style={{
          fontSize: fontSize || 24,
          color: colorToken ? getTokenVal(colorToken) : undefined,
        }}
      />
    </Tooltip>
  )
}

export interface CardTitleProps {
  title: ReactNode
  margin?: string
  button?: ReactNode
  alignItems?: string
  iconProps?: CardTitleIconProps
  iconPosition?: 'left' | 'right'
  hoverProps?: HoverProps
}

interface TitleContainerProps {
  $alignItems?: string
}

const TitleContainer = styled.div<TitleContainerProps>`
  display: flex;
  align-items: ${(props) => props.$alignItems || 'center'};
  gap: 8px;
  width: 100%;
`
const TitleButtonContainer = styled.div`
  margin-left: auto;
`

/**
 * @deprecated Use govwell-ui Header instead
 */
export const CardTitle = (props: CardTitleProps) => {
  const {
    title,
    margin,
    button,
    alignItems,
    iconProps,
    hoverProps = {},
    iconPosition = 'left',
  } = props
  const { getTokenVal } = useTheme()

  const renderedIcon = iconProps ? <CardTitleIcon {...iconProps} /> : null

  return (
    <TitleContainer $alignItems={alignItems} {...hoverProps}>
      {iconPosition === 'left' && renderedIcon}
      <AntdTitle level={5} style={{ margin: margin || 0 }} color={getTokenVal('colorTextHeading')}>
        {title}
      </AntdTitle>
      {iconPosition === 'right' && renderedIcon}
      {button && <TitleButtonContainer>{button}</TitleButtonContainer>}
    </TitleContainer>
  )
}

const StyledSpinContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`

export const LoadingSpin = forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({ width = 200, height = 200, ...props }, ref) => {
    return (
      <StyledSpinContainer {...props} ref={ref}>
        <LottieAnimation animationData={LottiesLoading} width={width} height={height} />
      </StyledSpinContainer>
    )
  }
)

interface AvatarProps {
  backgroundColorToken?: ColorToken
  borderColorToken?: ColorToken
  bottomLeftTail?: boolean
  children: ReactNode
  className?: string
  marginInlineEnd?: number
  size: number
}

interface AvatarContainerProps {
  $size: number
  $backgroundColor?: string
  $borderColor?: string
  $bottomLeftTail: boolean
  $marginInlineEnd?: number
}
const AvatarContainer = styled.div<AvatarContainerProps>`
  height: ${(props) => props.$size}px;
  min-height: ${(props) => props.$size}px;
  width: ${(props) => props.$size}px;
  min-width: ${(props) => props.$size}px;
  ${({ $marginInlineEnd }) =>
    $marginInlineEnd ? `margin-inline-end: ${$marginInlineEnd}px;` : ''};
  ${(props) => (props.$backgroundColor ? `background-color: ${props.$backgroundColor};` : '')};
  ${({ $borderColor }) => ($borderColor ? `border: solid 1px ${$borderColor};` : '')};
  border-radius: ${(props) => (props.$bottomLeftTail ? '50% 50% 50% 0' : '50%')};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const Avatar = ({
  backgroundColorToken,
  borderColorToken,
  bottomLeftTail,
  children,
  className,
  marginInlineEnd,
  size,
}: AvatarProps) => {
  const { getTokenVal } = useTheme()
  return (
    <AvatarContainer
      $size={size}
      $backgroundColor={backgroundColorToken ? getTokenVal(backgroundColorToken) : undefined}
      $borderColor={borderColorToken ? getTokenVal(borderColorToken) : undefined}
      $bottomLeftTail={!!bottomLeftTail}
      $marginInlineEnd={marginInlineEnd}
      className={twMerge('gw-avatar', className)}
    >
      {children}
    </AvatarContainer>
  )
}

export const AvatarImage = styled.img<{ $size: number }>`
  border-radius: 50%;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  min-width: ${(props) => props.$size}px;
  min-height: ${(props) => props.$size}px;
`

type RecordAddressProps = {
  asLink?: boolean
  // any record that extends mainBackwardsCompatibleLocationInformation
  record:
    | {
        mainBackwardsCompatibleLocationInformation?: BackwardsCompatibleLocationInformationFragment | null
      }
    | null
    | undefined
}

export const getRecordAddress = (args: RecordAddressProps) => {
  const { asLink = true, record } = args
  if (!record?.mainBackwardsCompatibleLocationInformation) {
    return ''
  }

  const label =
    record.mainBackwardsCompatibleLocationInformation.address?.str ||
    record.mainBackwardsCompatibleLocationInformation.parcelDisplayName

  if (!asLink) {
    return label
  }

  return (
    <Link
      internalRoute={backwardsCompatibleAddressUrl(
        record.mainBackwardsCompatibleLocationInformation
      )}
    >
      {label}
    </Link>
  )
}

export const RecordAddress = (props: RecordAddressProps) => {
  return getRecordAddress(props)
}

interface LabelSublabelOptionProps {
  label: ReactNode
  sublabel?: ReactNode
}
export const LabelSublabelOption = (props: LabelSublabelOptionProps) => {
  const { label, sublabel } = props
  return (
    <>
      <Text size={TextSize.Base} strong>
        {label}
      </Text>
      {(typeof sublabel === 'string' ? sublabel.trim() : sublabel) && (
        <Text
          size={TextSize.Small}
          style={{
            height: 'fit-content',
            display: 'flex',
            whiteSpace: 'break-spaces',
          }}
        >
          {sublabel}
        </Text>
      )}
    </>
  )
}

export const RightAlignedCellContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

interface FormContentContainerProps {
  $gap?: string
  $fullHeight?: boolean
}
const FormContentContainer = styled.div<FormContentContainerProps>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$gap || '24px'};
  ${(props) => props.$fullHeight && `height: 100%;`}
`

interface FormContainerProps<FormValues> {
  initialValues?: FormValues
  form: FormInstance<FormValues>
  children: ReactNode
  gap?: string
  fullHeight?: boolean
  fullWidth?: boolean
}
export function FormContainer<FormValues>(props: FormContainerProps<FormValues>) {
  const { initialValues, form, children, gap, fullHeight, fullWidth } = props
  const style = {
    ...(fullWidth ? { width: '100%' } : {}),
    ...(fullHeight ? { height: '100%' } : {}),
  }
  return (
    <Form
      {...(initialValues ? { initialValues } : {})}
      form={form}
      layout="vertical"
      requiredMark={RequiredMark}
      style={style}
      validateTrigger="onBlur"
    >
      <FormContentContainer $gap={gap} $fullHeight={fullHeight}>
        {children}
      </FormContentContainer>
    </Form>
  )
}

interface TabContainerProps {
  $gap?: string
  $minHeight?: string
  $hovered?: boolean
}
/**
 * @deprecated Stop using this
 */
export const TabContainer = styled.div<TabContainerProps>`
  ${(props) => getContainerStyleProps(props.theme)}
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$gap || '24px'};
  ${(props) => props.$minHeight && `min-height: ${props.$minHeight};`}
  ${(props) => props.$hovered && `border-color: ${props.theme.colorPrimaryBase};`}
  ${(props) => props.$hovered && `cursor: pointer;`}
`

interface HorizontalLineWithTextProps {
  text: string
}
const HorizontalLineWithTextContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`
export const HorizontalLineWithText = (props: HorizontalLineWithTextProps) => {
  const { text } = props
  return (
    <HorizontalLineWithTextContainer>
      <HorizontalLine />
      {text}
      <HorizontalLine />
    </HorizontalLineWithTextContainer>
  )
}

const IconContainer = styled.div`
  width: 32px;
  height: 32px;
  min-height: 32px;
  min-width: 32px;
  border-radius: 50%;
  background-color: ${(props) => props.theme['green-1']};
  border: 2px solid ${(props) => props.theme.colorSuccessBase};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const BoldedCheckMark = () => {
  const { getTokenVal } = useTheme()
  return (
    <IconContainer>
      <FontAwesomeIcon
        icon={faCheck}
        style={{
          color: getTokenVal('colorSuccessBase'),
          height: '16px',
          width: '16px',
        }}
      />
    </IconContainer>
  )
}

export const IconOnlyButton = styled(Button)`
  border: none;
`
export const FileButton = styled(IconOnlyButton)`
  background-color: transparent;
  svg {
    margin-top: 2px;
    width: 20px;
    height: 20px;
  }
`
